// src/pages/UsersPage.jsx

import React, { useState, useEffect } from "react";
import api from "../../services/api";
import {
  Table,
  Loader,
  Center,
  Alert,
  Title,
  ScrollArea,
  Button,
  Group,
  Avatar,
  Text,
  Badge,
  ActionIcon,
  Tooltip,
  Select, // <-- 1. Tambahkan Import Select
  TextInput
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import UserForm from "./UserForm";
import { IconPencil, IconTrash, IconFilter, IconSearch } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";

// --- Helper untuk warna Badge Role ---
const roleColors = {
  admin: 'red',
  penyedia: 'blue',
  sekolah: 'green',
  supervisor: 'orange',
};

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- 2. State untuk Filter ---
  const [filterRole, setFilterRole] = useState(null); // Menyimpan role yang dipilih
  const [searchQuery, setSearchQuery] = useState(""); // Opsional: Untuk pencarian nama

  const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.split(' ');
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get("/admin/users");
      setUsers(response.data);
      setError(null);
    } catch (err) {
      setError("Gagal mengambil data user. Anda mungkin tidak punya akses.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSaveSuccess = () => {
    closeModal();
    fetchUsers();
    notifications.show({ title: 'Sukses', message: 'Data user berhasil disimpan.', color: 'green' });
    setSelectedUser(null);
  };

  const handleTambah = () => {
    setSelectedUser(null);
    openModal();
  };
  
  const handleEdit = (user) => {
    setSelectedUser(user);
    openModal();
  };

  const handleDelete = async (id) => {
    if (window.confirm("Apakah Anda yakin ingin menghapus user ini?")) {
      try {
        await api.delete(`/admin/users/${id}`);
        notifications.show({ title: 'Sukses', message: 'User berhasil dihapus.', color: 'green' });
        fetchUsers();
      } catch (err) {
        notifications.show({ title: 'Error', message: 'Gagal menghapus user.', color: 'red' });
      }
    }
  };

  // --- 3. Logika Filtering Data ---
  // Kita membuat variabel baru 'filteredUsers' berdasarkan state filterRole
  const filteredUsers = users.filter((user) => {
    // Filter Role
    const matchRole = filterRole 
      ? user.role?.nama_role === filterRole 
      : true; // Jika filter kosong, anggap cocok (true)

    // Filter Search (Opsional, tapi sangat berguna)
    const matchSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                        user.email.toLowerCase().includes(searchQuery.toLowerCase());

    return matchRole && matchSearch;
  });

  if (loading && users.length === 0) {
    return (
      <Center style={{ height: 200 }}>
        <Loader />
      </Center>
    );
  }
  if (error) {
    return (
      <Alert color="red" title="Error!">
        {error}
      </Alert>
    );
  }

  // Gunakan 'filteredUsers' bukan 'users' untuk mapping baris tabel
  const rows = filteredUsers.map((user) => (
    <Table.Tr key={user.id}>
      <Table.Td>
        <Group gap="sm">
          <Avatar size={40} radius={40}>
            {getInitials(user.name)}
          </Avatar>
          <div>
            <Text fz="sm" fw={500}>{user.name}</Text>
            <Text fz="xs" c="dimmed">{user.email}</Text>
          </div>
        </Group>
      </Table.Td>

      <Table.Td>
        {user.role ? (
          <Badge 
            color={roleColors[user.role.nama_role] || 'gray'} 
            variant="light"
          >
            {user.role.nama_role}
          </Badge>
        ) : (
          <Text c="dimmed" fz="xs">N/A</Text>
        )}
      </Table.Td>
      
      <Table.Td>
        <Text fz="sm">
          {user.penyedia ? user.penyedia.nama_perusahaan : 
           (user.sekolah ? user.sekolah.nama_sekolah : 'N/A')}
        </Text>
      </Table.Td>

      <Table.Td>
        <Group gap="xs" justify="flex-end">
          <Tooltip label="Edit User">
            <ActionIcon variant="subtle" color="blue" onClick={() => handleEdit(user)}>
              <IconPencil size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Hapus User">
            <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(user.id)}>
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <>
      <Group justify="space-between" mb="md">
        <Title order={2}>Manajemen User</Title>
        <Button onClick={handleTambah}>
          + Tambah User
        </Button>
      </Group>

      {/* --- 4. Bagian Filter UI --- */}
      <Group mb="lg">
        {/* Filter Role */}
        <Select
          placeholder="Filter Role"
          leftSection={<IconFilter size={16} />}
          data={[
            { value: 'admin', label: 'Admin' },
            { value: 'penyedia', label: 'Penyedia' },
            { value: 'sekolah', label: 'Sekolah' },
            { value: 'supervisor', label: 'Supervisor' },
          ]}
          value={filterRole}
          onChange={setFilterRole}
          clearable // Agar bisa dikosongkan (menampilkan semua data)
          style={{ width: 200 }}
        />

        {/* Search Input (Bonus) */}
        <TextInput
          placeholder="Cari nama atau email..."
          leftSection={<IconSearch size={16} />}
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.currentTarget.value)}
          style={{ flex: 1 }} // Mengisi sisa ruang
        />
      </Group>

      <UserForm 
        opened={modalOpened} 
        onClose={() => {
          closeModal();
          setSelectedUser(null);
        }} 
        onSuccess={handleSaveSuccess}
        user={selectedUser} 
      />

      <ScrollArea>
        <Table highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>User</Table.Th>
              <Table.Th>Role</Table.Th>
              <Table.Th>Nama Profil</Table.Th>
              <Table.Th ta="right">Aksi</Table.Th>
            </Table.Tr>
          </Table.Thead>
          {/* Tampilkan pesan jika hasil filter kosong */}
          <Table.Tbody>
            {rows.length > 0 ? rows : (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Center h={100} c="dimmed">
                    Tidak ada data user yang sesuai filter.
                  </Center>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </>
  );
}